
/**
 * This is used for integration with Hilton's Fusion application
 * For testing, navigate to: /#/internal/connector
 * See also: MessagingModule, ConnectorModule
 */

import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { L10nTranslationService } from 'angular-l10n';
import { UserApi } from '../../hickory-api';
import { WindowMessage, WindowMessageService } from './window-message.service';
import { LogHelper } from '../logging/log-helper';

const tag = 'UserData';

@Injectable()
export class UserDataService {
  private readonly messageService = inject(WindowMessageService);
  private readonly translation = inject(L10nTranslationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbar = inject(MatSnackBar);
  private readonly userApi = inject(UserApi);

  private isInitialized: boolean = false;

  initialize() {
    if (!this.isInitialized) {
      this.isInitialized = true;

      // listen for changes to the route query params, watching for the "UserDataId" key
      this.route.queryParams.subscribe((params: Params) => {
        const paramKey = Object.keys(params).find(k => k.toLowerCase() === 'userdataid');
        const paramValue = paramKey ? params[paramKey] : null;
        if (paramValue) {
          this.loadMessage(paramValue);
        }
      });
    }
  }

  loadMessage(id: string) {
    this.userApi.getJsonById(id).subscribe({
      next: res => {
        if (res.isValid) {
          const message = res.item.json as WindowMessage;
          this.messageService.processMessage(message);
          this.removeMessage(res.item.id);
        }
      },
      error: err => {
        // Unable to retrieve message, can't perform action
        this.showSnackbar(this.translation.translate('NoDataFoundForId') + id);
        LogHelper.warn(tag, `loadMessage failed: ID=${id}`, err);

        // navigate to the dashboard instead
        void this.router.navigate(['/dashboard']);
      }
    });
  }

  removeMessage(id: string) {
    this.userApi.deleteJson(id).subscribe({
      next: _res => LogHelper.debug(tag, `removeMessage complete: ID=${id}`),
      error: err => LogHelper.warn(tag, `removeMessage failed: ID=${id}`, err)
    });
  }

  showSnackbar(message: string) {
    const config = new MatSnackBarConfig;
    config.duration = 5000;
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    this.snackbar.open(message, null, config);
  }
}
